import React, { useState, useEffect } from "react";
import logo from "../assets/logo.png";
import flagEl from "../assets/flag-el.png";
import flagEn from "../assets/flag-en.png";

export default function Navbar({ lang, setLang, t, isHomePage = true, theme = "dark", toggleTheme }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const mobileMenuStyle = {
    display: menuOpen ? "flex" : "none",
    flexDirection: "column",
    position: "absolute",
    top: "125px",
    left: 0,
    right: 0,
    backgroundColor: "var(--bg-secondary)",
    backdropFilter: "blur(20px)",
    borderBottom: "1px solid var(--border)",
    padding: "1.2rem 8vw",
    gap: "1.2rem",
    zIndex: 200,
  };

  return (
    <nav
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 8vw",
        background: scrolled
          ? "var(--nav-bg-scrolled)"
          : "var(--nav-bg-top)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: scrolled
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid transparent",
        position: "sticky",
        top: 0,
        zIndex: 100,
        flexWrap: "wrap",
        transition: "background 0.3s, border-color 0.3s, box-shadow 0.3s",
        boxShadow: scrolled ? "0 4px 24px rgba(0,0,0,0.4)" : "none"
      }}
    >
      {/* Logo + Desktop Links */}
      <div style={{ display: "flex", alignItems: "center", gap: "2.5rem" }}>
        <a href={`/?lang=${lang}`} style={{ textDecoration: "none", display: "flex", alignItems: "center" }}>
          <img
            src={logo}
            alt="KLEOS AI Logo"
            style={{
              height: 125,
              mixBlendMode: theme === "dark" ? "screen" : "multiply",
            }}
          />
        </a>

        <div className="desktop-menu" style={{ display: "flex", alignItems: "center" }}>
          <div style={{ display: "flex", gap: "0.25rem" }}>
            {isHomePage ? (
              <>
                <a href="#services" style={navLinkStyle}>{t.services}</a>
                <a href="#works" style={navLinkStyle}>{t.works}</a>
                <a href="#about" style={navLinkStyle}>{t.about}</a>
                <a href="#contact" style={navLinkStyle}>{t.contact}</a>
              </>
            ) : (
              <a href={`/?lang=${lang}`} style={{ ...navLinkStyle, color: "var(--accent-blue)" }}>
                ← {lang === "el" ? "Αρχική" : "Home"}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Right side: CTA + flags */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
        {/* Language flags */}
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }} className="lang-buttons">
          <button
            onClick={() => setLang("el")}
            aria-label="Ελληνικά"
            style={{ border: "none", background: "none", cursor: "pointer", padding: "2px", borderRadius: "4px" }}
          >
            <img src={flagEl} alt="Ελληνικά" style={{ height: 18, filter: lang === "el" ? "none" : "grayscale(100%) opacity(0.4)" }} />
          </button>
          <button
            onClick={() => setLang("en")}
            aria-label="English"
            style={{ border: "none", background: "none", cursor: "pointer", padding: "2px", borderRadius: "4px" }}
          >
            <img src={flagEn} alt="English" style={{ height: 18, filter: lang === "en" ? "none" : "grayscale(100%) opacity(0.4)" }} />
          </button>
        </div>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: "8px",
            cursor: "pointer",
            padding: "0.38rem 0.5rem",
            fontSize: "1rem",
            lineHeight: 1,
            transition: "background 0.2s, border-color 0.2s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          {theme === "dark" ? "☀️" : "🌙"}
        </button>

        {/* CTA */}
        {isHomePage && (
          <a
            href="#contact"
            aria-label={t.nav_cta}
            className="nav-cta-btn"
            style={{
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              color: "#fff",
              padding: "0.5rem 1.1rem",
              borderRadius: "10px",
              fontWeight: "600",
              fontSize: "0.85rem",
              textDecoration: "none",
              whiteSpace: "nowrap",
              boxShadow: "0 2px 12px rgba(139,92,246,0.4)",
              transition: "transform 0.2s, box-shadow 0.2s, filter 0.2s"
            }}
          >
            {t.nav_cta}
          </a>
        )}

        {/* Hamburger */}
        <button
          className="hamburger-icon"
          style={{
            display: "none",
            background: "none",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: "8px",
            cursor: "pointer",
            padding: "0.4rem 0.6rem",
            color: "var(--text-primary)",
            fontSize: "1.1rem",
            lineHeight: 1
          }}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? "✕" : "☰"}
        </button>
      </div>

      {/* Mobile menu */}
      <div style={mobileMenuStyle} className="mobile-menu">
        {isHomePage ? (
          <>
            <a href="#services" onClick={() => setMenuOpen(false)} style={mobileLinkStyle}>{t.services}</a>
            <a href="#about" onClick={() => setMenuOpen(false)} style={mobileLinkStyle}>{t.about}</a>
            <a href="#contact" onClick={() => setMenuOpen(false)} style={mobileLinkStyle}>{t.contact}</a>
            <a href="#contact" onClick={() => setMenuOpen(false)} style={{
              ...mobileLinkStyle,
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              color: "#fff",
              borderRadius: "10px",
              padding: "0.7rem 1rem",
              textAlign: "center",
              fontWeight: "600"
            }}>{t.nav_cta}</a>
          </>
        ) : (
          <a href={`/?lang=${lang}`} onClick={() => setMenuOpen(false)} style={{ ...mobileLinkStyle, color: "var(--accent-blue)" }}>
            ← {lang === "el" ? "Αρχική Σελίδα" : "Home"}
          </a>
        )}
      </div>

      <style>{`
        @media (min-width: 769px) {
          .mobile-menu { display: none !important; }
          .hamburger-icon { display: none !important; }
          .desktop-menu { display: flex !important; }
        }
        @media (max-width: 768px) {
          .desktop-menu { display: none !important; }
          .hamburger-icon { display: block !important; }
          .nav-cta-btn { display: none !important; }
        }
        .nav-cta-btn:hover {
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 18px rgba(139,92,246,0.6) !important;
          filter: brightness(1.1) !important;
          color: #fff !important;
        }
      `}</style>
    </nav>
  );
}

const navLinkStyle = {
  padding: "0.45rem 0.75rem",
  borderRadius: "8px",
  fontSize: "0.9rem",
  fontWeight: "500",
  color: "var(--text-secondary)",
  transition: "color 0.2s, background 0.2s",
  textDecoration: "none"
};

const mobileLinkStyle = {
  color: "var(--text-secondary)",
  fontSize: "1rem",
  fontWeight: "500",
  padding: "0.5rem 0",
  textDecoration: "none"
};
